import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";
import { asyncLoadScript } from "../../../utils/common";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { zPad } from "../../../utils/number";
import { useGetDeviceListingQuery } from "../../../api/network/camerasAdminVMApiService";
import { useLazyGReverseGeocodeQuery } from "../../../api/network/mapApiServices";

type ModalVMCameraLiveviewProps = {
  cameraSerialNumber: string;
  onHideCallback?: () => void;
};

const debugComponent = true;

const ModalVMCameraLiveview: FC<ModalVMCameraLiveviewProps> = ({
  cameraSerialNumber,
  onHideCallback,
}) => {
  const { t } = useTranslation();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const liveviewDurationSeconds =
    loggedInUser?.account?.cameraliveviewduration ?? 30;


  const initRef = React.useRef(false);
  const [progressValue, setProgressValue] = React.useState<number>(0);
  const [liveviewError, setLiveviewError] = React.useState<
    ReactNode | string | null
  >(null);

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const [sessionStable, setSessionStable] = React.useState(false);
  const [timerSeconds, setTimerSeconds] = useState<number>(
    liveviewDurationSeconds
  );
  const timerIntervalRef = React.useRef<any>(null);

  // Address state and API hooks
  const [address, setAddress] = useState<string>("");
  const [gReverseGeocodeAPITrigger] = useLazyGReverseGeocodeQuery();

  // Fetch device listing to get lat/lng - search by exact serial number
  const { data: deviceListingData } = useGetDeviceListingQuery(
    {
      keyword: "",
      model: "",
      status: "",
      offset: 0,
      limit: 1000,
    },
    {
      skip: !cameraSerialNumber,
    }
  );

  // Fetch address based on device location
  useEffect(() => {
    const fetchAddress = async () => {
      // Find the exact device by serial number
      const device = deviceListingData?.data?.find(
        (d) => d.serial_number === cameraSerialNumber
      );

      if (device?.ft_lastvalidlatitude && device?.ft_lastvalidlongitude) {
        try {
          if (!!device.ft_deviceaddress) {
            setAddress(device.ft_deviceaddress);
          } else {
            const { results } = await gReverseGeocodeAPITrigger({
              latitude: device.ft_lastvalidlatitude,
              longitude: device.ft_lastvalidlongitude,
            }).unwrap();
            if (results?.[0]?.formatted_address) {
              setAddress(results[0].formatted_address);
            }
          }
        } catch (error) {
          console.error("Error fetching address:", error);
          setAddress("");
        }
      } else {
        setAddress("");
      }
    };

    if (deviceListingData?.data && cameraSerialNumber) {
      fetchAddress();
    }
  }, [deviceListingData, gReverseGeocodeAPITrigger, cameraSerialNumber]);

  const clearTimerInterval = () => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log("== [clearTimerInterval] timer interval cleared");
    window.clearInterval(timerIntervalRef.current!);
  };
  const intervalCallback = React.useCallback(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("_");
    setTimerSeconds((time) => time - 1);
    // setTimerSeconds(timerSeconds - 1);
  }, [timerSeconds]);
  const initTimerInterval = () => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log(
        "== [initTimerInterval] timer started",
        timerIntervalRef.current
      );
    timerIntervalRef.current = window.setInterval(intervalCallback, 1000);
  };
  // }, [modalsState, cameraSerialNumber]);

  useEffect(() => {
    if (
      sessionStable &&
      timerIntervalRef.current === null &&
      timerSeconds === liveviewDurationSeconds
    ) {
      // Start the timer when the component mounts
      initTimerInterval();
    }
  }, [sessionStable, timerSeconds, timerIntervalRef.current, liveviewDurationSeconds]);

  // clear interval on unmount
  useEffect(() => {
    return () => clearTimerInterval();
  }, []);

  useEffect(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("== [timerSeconds]", timerSeconds);
    if (timerSeconds <= 0) {
      // Stop the timer when it reaches zero
      clearTimerInterval();
      const vmRTC = (window as any).vmRTC;
      if (vmRTC) {
        // Stop the liveview session
        vmRTC.stop();
      }
      // close the modal and dispatch the action to show continue modal
      setSessionStable(false);
      setLiveviewError("Session expired");
      dispatch(
        setModalsData({
          ...modalsState,
          showModalVMCameraLiveviewSN: null,
          showModalVMCameraLiveviewContinueSN: cameraSerialNumber,
        })
      );
    }
  }, [timerSeconds, cameraSerialNumber, modalsState]);

  const TimerDisplayComponent = useCallback(() => {
    return (
      <div className="font-bold text-white/70 text-lg ps-3 pe-1">
        {`${Math.floor(timerSeconds / 60)}:${zPad(timerSeconds % 60, 2)}`}
      </div>
    );
  }, [timerSeconds]);

  const webRTCConfig = {
    // "staging" | "prod"
    env: loggedInUser?.config?.VISIONMAX_BASE_URL?.includes("staging")
      ? "staging"
      : "prod",
    // VM_ENV === "prods"
    //   ? process.env.REACT_APP_VISIONMAX_ENV
    //   : process.env.REACT_APP_VISIONMAX_ENV_STAGING,
    apiKey: loggedInUser?.config?.VISIONMAX_X_API_KEY,
    // VM_ENV === "prod"
    //   ? process.env.REACT_APP_VISIONMAX_KEY
    //   : process.env.REACT_APP_VISIONMAX_KEY_STAGING,
    clientAccessToken: localStorage.getItem(
      localStorageKeys.camerasAdminVMToken
    ),
    feature: {
      enableDefaultCameraSwitchButton: true,
    },
  };

  const initLiveview = () => {
    if (initRef.current) return;

    // update the ref to ensure the code executes only once
    initRef.current = true;

    // initialize scripts start here
    if (APP_CONFIG.DEBUG.ALL || debugComponent)
      console.log("%c== [initLiveview]", "background-color:red;color:white");

    // import the webRTC script from `public/js` directory
    // ---------------------------------------------------
    // other dependencies (aws-sdk-2.1490.0.min.js, mqttws31.js and adapter.js)
    // are already loaded in the index.html file
    // a copy of each of these files is archived in `src/components/visionmax-liveview-ui/archive/` directory
    asyncLoadScript(
      `${process.env.PUBLIC_URL}/js/vmRTC-umd.js`,
      "visionmax-liveview-webrtc",
      () => {
        if (APP_CONFIG.DEBUG.ALL || debugComponent)
          console.log("== [initLiveview] webrtc script loaded");
        try {
          // initialize the webRTC
          const vmRTC = (window as any).vmRTC;
          if (vmRTC) {
            // bind event handlers
            vmRTC.addEventListener("progressStatusUpdated", (input: any) => {
              // setProgressValue(parseInt(input, 10));
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [progressStatusUpdated]", input);
              setProgressValue(input);
            });
            //to catch event of connection fail
            vmRTC.addEventListener("connect fail", (input: any) => {
              vmRTC.stop();
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [connect fail]", input);
              if (!(window as any).retryingLiveview)
                setLiveviewError("connect fail");
            });
            vmRTC.addEventListener("session stable", (input: any) => {
              (window as any).liveviewSessionStable = true;
              if (!!(window as any)[`liveviewAttempt_${cameraSerialNumber}`]) {
                delete (window as any)[`liveviewAttempt_${cameraSerialNumber}`];
              }
              setSessionStable(true);
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [session stable]", input);
            });
            //to catch event of connection lost
            vmRTC.addEventListener("connection lost", () => {
              vmRTC.stop();
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [connection lost]");
              setLiveviewError("connection lost");
            });

            vmRTC.addEventListener("initial.fail", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [initial.fail]", input);
              setLiveviewError("initial fail");
            });

            vmRTC.addEventListener("broker disconnnected", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [broker disconnnected]", input);
              // connection was, probably, unsuccessful after a wakeup
              if (
                !(window as any).liveviewSessionStable &&
                (window as any)[`liveviewAttempt_${cameraSerialNumber}`] <
                  APP_CONFIG.CAMERA_ADMIN.LIVEVIEW_RETRIES
              ) {
                hideAndReInitialize();
              }
            });

            // setup and start
            if (APP_CONFIG.DEBUG.ALL || debugComponent) {
              console.log("== [SERIAL_NUMBER]", cameraSerialNumber);
              console.log(
                "%c== [LIVEVIEW setup() CALLED]",
                "background-color:red;color:white;"
              );
            }
            vmRTC.setup(webRTCConfig).start(cameraSerialNumber);
            (window as any)[`liveviewAttempt_${cameraSerialNumber}`] = !!(
              window as any
            )[`liveviewAttempt_${cameraSerialNumber}`]
              ? (window as any)[`liveviewAttempt_${cameraSerialNumber}`] + 1
              : 1;
          } else {
            console.error("== [initLiveview] vmRTC is not defined");
          }
        } catch (e) {
          if (APP_CONFIG.DEBUG.ALL) console.error("== ERR", e);
        }
      }
    );
  };
  useEffect(() => {
    initLiveview();
    return () => {
      (window as any).liveviewSessionStable = false;
      // (document as any).querySelector('script[src*="vmRTC-umd.js"]').remove();
      // (window as any).vmRTC.stop();
      // delete (window as any).vmRTC;
    };
  }, []);

  // this function is deprecated
  // the library renders a next cam button on the page and
  // we re-skin it to appear similar to our UI
  // const handleNextCamClicked = () => {
  //   const vmRTC = (window as any).vmRTC;
  //   if (vmRTC) {
  //     vmRTC.nextCam();
  //   }
  // };

  const hideAndReInitialize = () => {
    const cameraSN = modalsState.showModalVMCameraLiveviewSN;
    (window as any).retryingLiveview = true;
    setLiveviewError(
      <span className="flex flex-col gap-6 text-center">
        <span>Connection with the camera could not be established.</span>
        <span>Retrying in 15 seconds...</span>
      </span>
    );
    setTimeout(() => {
      // check camera status here and then do the needful...
      // 7/7/2025 found that without checking camera status here, the liveview still works in wakeup flow
      hideModal();
      setTimeout(() => {
        (window as any).retryingLiveview = false;
        dispatch(
          setModalsData({
            ...modalsState,
            showModalVMCameraLiveviewSN: cameraSN,
          })
        );
      }, 200);
    }, 15 * 1000);
  };

  const hideModal = () => {
    const vmRTC = (window as any).vmRTC;
    if (vmRTC) {
      vmRTC.stop();
    }
    onHideCallback?.();
    dispatch(
      setModalsData({ ...modalsState, showModalVMCameraLiveviewSN: null })
    );
  };

  const VideoElement = React.useCallback(() => {
    return (
      <video
        className={`absolute z-[1] top-0 left-0 w-full h-full`}
        id="rtc_liveview"
        autoPlay
        playsInline
        disablePictureInPicture={true}
      ></video>
    );
  }, [modalsState.showModalVMCameraLiveviewSN]);

  if (modalsState.showModalVMCameraLiveviewSN === null) return null;

  return (
    <>
      <div
        className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
        onClick={hideModal}
      />
      <div
        id="visionmax-liveview"
        className={
          "fixed inset-y-1/2 inset-x-1/2 w-[calc(100vw-6rem)] h-[calc(100vh-6rem)] bg-black/80 overflow-hidden " +
          "Xbg-white rounded-xl shadow-lg z-modal"
        }
        style={{ transform: "translate(-50%, -50%)" }}
      >
        {/* Camera Buttons Area */}
        <div className="absolute top-4 right-4 flex items-center flex-row gap-2 z-50 p-2 bg-white/20 rounded-full">
          {/* {vehicleLiveCamerasData?.cameras.map((cameraId) => (
          <button
            key={cameraId}
            className={
              "size-10 flex items-center justify-center rounded-full focus:ring focus:ring-white outline-0 " +
              (cameraOnDisplay === cameraId ? "bg-field-success text-white" : "bg-white/50 hover:bg-white")
            }
            onClick={() => setCameraOnDisplay(cameraId)}
          >
            {cameraId}
          </button>
        ))} */}
          {!liveviewError && !!sessionStable && (
            <>
              {address && (
                <div
                  className="font-medium text-white/90 text-sm px-3"
                  title={address}
                >
                  {address}
                </div>
              )}
              {address && <div className="w-px h-10 bg-white/15" />}
              <TimerDisplayComponent />
              <button
                key={cameraSerialNumber}
                className={
                  "h-10 flex items-center justify-center rounded-full focus:ring focus:ring-white outline-0 " +
                  "bg-white/50 enabled:hover:bg-white " +
                  "px-4 uppercase font-bold invisible" // made invisible to prevent this placeholder button to interfere with the VM SDK button hover states
                }
                onClick={() => {}}
                disabled={true}
              >
                {t("next")}
              </button>
              <div className="w-px h-10 bg-white/15" />
            </>
          )}
          <button
            className="group size-10 flex items-center justify-center rounded-full bg-black hover:bg-gray-100 focus:ring focus:ring-white outline-0"
            onClick={hideModal}
          >
            <img
              src={svgIcons.CloseIcon}
              alt="Close"
              className="grayscale-0 brightness-0 invert group-hover:invert-0 opacity-50"
            />
          </button>
        </div>

        {/* Progress bar or error message display */}
        {!!liveviewError ? (
          <p className="absolute z-[2] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg uppercase font-bold text-gray-200/70 text-center">
            {liveviewError}
          </p>
        ) : progressValue < 100 ? (
          <div className="absolute z-[2] top-0 left-0 w-full h-full flex items-center justify-center">
            <div
              className="w-[calc(100%-6rem)] border border-gray-400/70 rounded-md h-12 relative"
              role="progressbar"
              aria-label={`loading progress`}
              aria-valuemin={0}
              aria-valuenow={progressValue}
              aria-valuemax={100}
            >
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-200/70">
                {progressValue}%
              </span>
              <div
                className="absolute top-0 left-0 h-full bg-gray-400/25"
                style={{ width: `${progressValue}%` }}
              ></div>
            </div>
          </div>
        ) : null}

        {/* Video element */}
        {VideoElement()}
        {/* <VideoElement /> */}
      </div>
    </>
  );
};
export default ModalVMCameraLiveview;
